'use client';

import { Play } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ElasticArticleItem } from '@//app/api/articles';
import { ArticleCard, type ArticleCardSize } from '@//components/ArticleCard';
import { Badge } from '@//components/Badge';
import { ArticlePreviewTracker } from '@//containers/ViewPortTracker/ArticlePreviewTracker';
import { useLegacyTrackNewsItemView } from '@//data/tracking/useLegacyTrackNewsItemView';
import useFormatDate from '@//hooks/useFormatDate';
import { calculateReadingTime } from '@//utils/calculateReadingTime';
import { FormatDateOption } from '@//utils/date';
import { slugify } from '@//utils/slugify';

interface ArticleCardProps {
  article: ElasticArticleItem;
  rootUrl: string;
  hideExcerpt?: boolean;
  size?: ArticleCardSize;
  fetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  loading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
}

const ArticleCardContainer = ({
  article,
  rootUrl,
  hideExcerpt,
  size,
  fetchPriority,
  loading,
}: ArticleCardProps) => {
  const tCommon = useTranslations('other.common');
  const { trackLegacyNewsItemView } = useLegacyTrackNewsItemView();
  const { formatDate } = useFormatDate();

  const formattedDate = formatDate(new Date(article.createdAt), FormatDateOption.DayMonthYear);

  const readingTime = calculateReadingTime(article.content || '');

  return (
    <>
      <ArticlePreviewTracker article={article} />
      <ArticleCard
        id={article.id}
        testId={article.id}
        image={
          article.featuredMedia?.file
            ? {
                src: `${article.featuredMedia.file.url}?w=416&h=234&q=85&fit=fill`,
                srcSet: `
            ${article.featuredMedia.file.url}?w=300&h=169&q=85&fit=fill 300w,
            ${article.featuredMedia.file.url}?w=416&h=234&q=85&fit=fill 416w,
            ${article.featuredMedia.file.url}?w=640&h=360&q=85&fit=fill 600w,
            ${article.featuredMedia.file.url}?w=800&h=450&q=85&fit=fill 800w,
          `,
                webpSrcSet: `
            ${article.featuredMedia.file.url}?w=300&h=169&q=85&fit=fill&fm=webp 300w,
            ${article.featuredMedia.file.url}?w=416&h=234&q=85&fit=fill&fm=webp 416w,
            ${article.featuredMedia.file.url}?w=640&h=360&q=85&fit=fill&fm=webp 600w,
            ${article.featuredMedia.file.url}?w=800&h=450&q=85&fit=fill&fm=webp 800w,
          `,
                sizes: `
            (min-width: 2001px) 416px,
            (min-width: 1012px) calc(33vw - 7rem),
            (min-width: 696px) calc(50vw - 6rem),
            (min-width: 592px) calc(100vw - 5rem),
            calc(100vw - 2rem)
          `,
                alt: article.title,
                width: 416,
                height: 234,
                fetchPriority,
                loading,
              }
            : undefined
        }
        href={`${rootUrl}/${slugify(article.categories?.[0].name)}/${article.slug}`}
        onClick={() => trackLegacyNewsItemView(article.id, article.title)}
        title={article.title}
        excerpt={!hideExcerpt ? article.excerpt : undefined}
        date={formattedDate}
        readingTime={readingTime}
        preTitle={
          article.categories
            ? article.categories.map((category) => category.name).join(', ')
            : undefined
        }
        size={size}
        meta={
          article.videoUrl ? (
            <Badge size="sm" tone="brand">
              <Play aria-hidden="true" />
              {tCommon('video')}
            </Badge>
          ) : undefined
        }
      />
    </>
  );
};

export default ArticleCardContainer;
